import React, { useEffect, useState } from "react";
import StepStatus from "../StepStatus";
import SubTitle from "../SubTitle";
import { Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { allCheckedSelector, checkListState, checkListGrouped, singleCheckedSelector } from "../../../recoil/report/checkListState";
import { TextField, MenuItem } from "@mui/material";
import { reportFilterState } from "../../../recoil/report/reportFilterState";
import { checkListRaw } from "../../../constant";

const Step1 = () => {

  const [filterOptions, setFilterOptions] = useState({});

  const checkList = useRecoilValue(checkListState);
  const [allChecked, setAllChecked] = useRecoilState(allCheckedSelector);
  const setSingleChecked = useSetRecoilState(singleCheckedSelector);

  const handleSelectAll = () => setAllChecked(prev => !prev);

  const handleCheckboxChange = (category, label) => setSingleChecked({ category, label });

  const [filters, setFilters] = useRecoilState(reportFilterState);

  // Get filter config for a given label from checkListRaw
  const getFiltersConfig = (label) =>
    checkListRaw.find((x) => x.label === label)?.filters || [];

  // Update a specific filter value
  const updateFilterValue = (label, field, value) => {
    setFilters((prev) => ({
      ...prev,
      [label]: {
        ...(prev[label] || {}),
        [field]: value,
      },
    }));
  };

  useEffect(() => {
    // Find all filters that have an API attached (e.g., Categories)
    const filtersWithApi = [];

    checkListRaw.forEach((item) => {
      (item.filters || []).forEach((f) => {
        if (f.api) {
          filtersWithApi.push({
            label: item.label,
            field: f.field,
            apiFn: f.api,
          });
        }
      });
    });

    filtersWithApi.forEach(({ label, field, apiFn }) => {
      const key = `${label}.${field}`;

      // set loading true
      setFilterOptions((prev) => ({
        ...prev,
        [key]: { ...(prev[key] || {}), loading: true },
      }));

      apiFn()
        .then((res) => {

          const options = (res || []).map((x) => ({
            value: x.id,
            label: x.text,
          }));

          setFilterOptions((prev) => ({
            ...prev,
            [key]: { loading: false, options, error: null },
          }));

          setFilters((prev) => {
            const existing = prev[label]?.[field];

            // Don't override if user already picked something
            if (Array.isArray(existing) && existing.length > 0) {
              return prev;
            }

            const allValues = options.map((o) => o.value);

            return {
              ...prev,
              [label]: {
                ...(prev[label] || {}),
                [field]: allValues,
              },
            };
          });

        })
        .catch((err) => {
          console.error("Failed to load filter options", label, field, err);
          setFilterOptions((prev) => ({
            ...prev,
            [key]: { loading: false, options: [], error: err },
          }));
        });
    });
  }, []);


  return (
    <div>
      <StepStatus
        currentStatus={1}
        currentTitle={"Select Information to Include"}
      />

      {/* Select All CheckBox  */}
      <div className="flex justify-end items-center">
        <FormControlLabel
          control={<Checkbox
            checked={allChecked}
            onClick={handleSelectAll}
          />}
          label={"Select All"}
        />
      </div>

      {/* All Checkboxes  */}
      {Object.keys(checkListGrouped).map((category, i) => (
        <div key={i} className="mt-7">
          <SubTitle title={category} />
          <FormGroup>
            {checkListGrouped[category].map((item, j) => {
              const isChecked = checkList[category].includes(item.label);
              const itemFilters = getFiltersConfig(item.label);

              return (
                <div key={j} className="mb-2">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isChecked}
                        onChange={() =>
                          handleCheckboxChange(category, item.label)
                        }
                      />
                    }
                    label={item?.label}
                  />

                  {/* Render filters ONLY for Journaling items that are checked */}
                  {category === "Journaling" &&
                    isChecked &&
                    itemFilters.length > 0 && (
                      <div className="ml-8 mt-1 space-y-2">
                        {itemFilters.map((f) => {
                          const currentValue =
                            filters[item.label]?.[f.field];

                          // Checkbox filter (e.g. Is Important)
                          if (f.type === "checkbox") {
                            return (
                              <FormControlLabel
                                key={f.field}
                                control={
                                  <Checkbox
                                    checked={!!currentValue}
                                    onChange={(_, checked) =>
                                      updateFilterValue(
                                        item.label,
                                        f.field,
                                        checked
                                      )
                                    }
                                  />
                                }
                                label={f.label}
                              />
                            );
                          }

                          // Multi-select filter (e.g. Categories) rendered as checkboxes,
                          // grouped by the filter's own label (f.label), not from the API.
                          if (f.type === "select-multiple") {
                            const key = `${item.label}.${f.field}`;
                            const entry = filterOptions[key] || {};
                            const options = entry.options || [];
                            const loading = entry.loading;

                            const selectedValues = currentValue || [];

                            return (
                              <div key={f.field} className="mt-2">
                                {/* Main title of this filter */}
                                <div className="text-sm font-semibold mb-1">
                                  {loading ? `${f.label} (loading...)` : f.label}
                                </div>

                                {/* Group header from filters --> label */}
                                <div className="mb-1">

                                  <FormGroup>
                                    {options.map((opt) => {
                                      const isChecked = selectedValues.includes(opt.value);

                                      return (
                                        <FormControlLabel
                                          key={opt.value}
                                          control={
                                            <Checkbox
                                              size="small"
                                              checked={isChecked}
                                              onChange={(_, checked) => {
                                                const prev = selectedValues;
                                                const next = checked
                                                  ? [...prev, opt.value]
                                                  : prev.filter((v) => v !== opt.value);

                                                updateFilterValue(item.label, f.field, next);
                                              }}
                                            />
                                          }
                                          label={opt.label}
                                        />
                                      );
                                    })}
                                  </FormGroup>
                                </div>
                              </div>
                            );
                          }


                          return null;
                        })}
                      </div>
                    )}
                </div>
              );
            })}
          </FormGroup>
        </div>
      ))}
    </div>
  );
};

export default Step1;
